
import { closeModal, openModal } from '@ds/components/ModalBase'
import { mapGetters } from 'vuex'

import { MODAL_NAMES } from '@config/constants'
import { createObserver } from '@core/helpers/vue-Intersection-observer'
import { addProductsToCart } from '@http/endpoints'
import { logger } from '@recommendation/helpers'
import recommendationAPIClient from '@recommendation/recommendationAPIClient'
import { toTrackingData } from '@recommendation/recommendationAdapter'
import {
  PRODUCT_DEAL_TYPE,
  PRODUCT_PROVIDERS,
  PRODUCT_SOURCE,
  PRODUCT_SOURCE_TYPE,
} from '@tracking'
import {
  trackAddToCartBatch,
  trackErrors,
  trackProductImpressionBatched,
} from '@tracking/events'

import BundleLarge from './BundleLarge/BundleLarge.vue'
import BundleSmall from './BundleSmall/BundleSmall.vue'
import { STATUSES } from './RecommendationBundle.constants'
import {
  getTotalPrice,
  productToBundle,
  recommendationToBundle,
} from './RecommendationBundle.helpers'

export default {
  components: {
    BundleLarge,
    BundleSmall,
  },
  inject: ['openErrorToast'],
  props: {
    activeGrade: {
      type: Object,
      required: true,
    },
    mainProduct: {
      type: Object,
      required: true,
    },
    mainProductTrackingInfo: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: null,
    },
    limit: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      status: STATUSES.FETCHING,
      recommendedBundledProducts: [],
      widgetId: null,
    }
  },
  computed: {
    ...mapGetters({
      experiments: 'flags/getExperiments',
    }),
    mainListing() {
      return {
        ...productToBundle(this.mainProduct, this.activeGrade, this.$t.price),
        trackingData: this.mainProductTrackingData,
      }
    },
    recommendedListings() {
      return this.recommendedBundledProducts.map((product, index) => ({
        ...recommendationToBundle(product, this.$t.price),
        trackingData: this.recommendedBundledProductsTrackingData[index],
      }))
    },
    listings() {
      if (this.status === STATUSES.FETCHING) {
        return Array.from({ length: this.limit + 1 }, () => ({}))
      }

      return [this.mainListing, ...this.recommendedListings]
    },
    recommendedBundledProductsTrackingData() {
      return this.recommendedBundledProducts
        .map((product, index) => ({
          ...toTrackingData(product, {
            dealType: PRODUCT_DEAL_TYPE.NORMAL,
            list: PRODUCT_SOURCE_TYPE.CROSS_SELL_BUNDLE,
            position: index + 2,
            provider: PRODUCT_PROVIDERS.EARLYBIRD,
            source: PRODUCT_SOURCE.RECOMMENDATIONS,
            widgetId: this.widgetId,
          }),
        }))
        .map((r) => ({
          ...r.product,
          mainListing: this.activeGrade.id,
        }))
    },
    mainProductTrackingData() {
      return {
        ...this.mainProductTrackingInfo,
        quantity: 1,
        list: PRODUCT_SOURCE_TYPE.CROSS_SELL_BUNDLE,
        position: 1,
      }
    },
    trackingData() {
      return [
        this.mainProductTrackingData,
        ...this.recommendedBundledProductsTrackingData,
      ]
    },
    totalPrice() {
      if (this.listings.every((listing) => listing.price?.raw?.amount)) {
        try {
          return getTotalPrice(this.listings, this.$t.price)
        } catch (err) {
          logger.info(err, {
            component: { name: 'RecommendationBundle', props: this.$props },
            routeName: this.$route.name,
          })
        }
      }

      return null
    },
    noFetchError() {
      return this.status !== STATUSES.ERROR_FETCHING
    },
  },
  watch: {
    activeGrade(newVal, oldVal) {
      if (newVal.id !== oldVal.id) {
        closeModal(MODAL_NAMES.PRODUCT.BUNDLE)
        this.fetchRecommendedBundledProducts()
      }
    },
  },
  destroyed() {
    if (typeof this.unobserve === 'function') {
      this.unobserve()
    }
  },
  mounted() {
    this.fetchRecommendedBundledProducts()
    this.watchBundleInViewport()
  },
  updated() {
    this.watchBundleInViewport()
  },
  methods: {
    async fetchRecommendedBundledProducts() {
      try {
        this.status = STATUSES.FETCHING
        const { products, widgetId } = await recommendationAPIClient(
          (request) => this.$store.dispatch('http/request', request),
        ).getRecommendedBundledProducts({
          limit: this.limit,
          listingId: this.mainListing.id,
          recoInGrade: this.experiments.recoInGrade,
        })

        if (products.length === 0) {
          throw new Error(
            `No available bundle recommendation for product #${this.mainListing.id} in widget #${widgetId}`,
          )
        }

        this.recommendedBundledProducts = products
        this.widgetId = widgetId
        this.status = this.$route.query.bundleStatus || STATUSES.FETCHED
      } catch (err) {
        this.status = STATUSES.ERROR_FETCHING

        logger.info(err, {
          component: { name: 'RecommendationBundle', props: this.$props },
          routeName: this.$route.name,
        })
      }
    },
    watchBundleInViewport() {
      if (this.unobserve) {
        this.unobserve()
      }

      if (this.status === STATUSES.FETCHED) {
        this.unobserve = createObserver(this.$el, () => {
          trackProductImpressionBatched(this.trackingData)
        })
      }
    },
    async addToCart({ withToast = true } = {}) {
      this.status = STATUSES.ADDING_TO_CART

      try {
        const listings = this.listings.map((listing) => ({
          listing_id: listing.id,
        }))

        if (listings.length === 0) {
          throw new Error('No available listings in the add to cart method')
        }

        await this.$store.dispatch('http/request', {
          request: addProductsToCart,
          body: {
            listings,
          },
        })

        trackAddToCartBatch({
          products: this.trackingData,
        })

        this.status = STATUSES.ADDED_TO_CART
        this.$emit('add-to-cart', {
          additionalListings: this.recommendedListings,
        })

        openModal(MODAL_NAMES.PRODUCT.ADD_TO_CART)
      } catch (err) {
        if (withToast) {
          this.openErrorToast()
        }
        this.status = STATUSES.ERROR_ADDING_TO_CART

        logger.info(err, {
          component: { name: 'RecommendationBundle', props: this.$props },
          routeName: this.$route.name,
        })

        trackErrors({
          zone: 'bundle_add_to_cart',
          message: err?.payload?.detail ?? 'unknown_error',
        })
      }
    },
  },
}
